import {
  Button,
  Modal,
  PasswordInput,
  Select,
  Switch,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { TemporaryPasswordPanel } from '../settings/UsersScreen.js';
import classes from './CrewCardDialog.module.css';
import { CrewMedicalEditor, CrewPhotoField, CrewPhysicalFields } from './CrewParticulars.js';
import {
  type CrewCardForm,
  crewCardErrorKey,
  crewCardReady,
  crewCardUpdate,
  emptyCrewCardForm,
  formOfProfile,
  nationalityOptions,
  newCrewCardInput,
} from './crew-profile.js';
import { type Option, SignOnFields, SignOnWarnings, useRanks } from './MovementDialogs.js';
import { movementErrorKey } from './sign-on-off.js';
import {
  type CrewCardEditor,
  type CrewMovementEditor,
  errorCode,
  type PersonnelSource,
} from './source.js';
import type { CrewCardResult, CrewProfileView, RankOption } from './types.js';

type Mode = 'new' | 'edit';

/** Pencerenin sekmeleri (sahip 2026-11-04 B2; ADR 0158). */
export type CrewCardTab = 'personal' | 'physical' | 'medical';

/**
 * Görünen sekmeler: kişisel ve kıyafet/fiziksel her zaman; sağlık yalnız düzenlemede, görme
 * yetkisiyle (`crew_medical.view`) ve bağdaştırıcıda okuma varsa (ADR 0158 §2).
 */
export function crewCardTabs(
  mode: Mode,
  view: Pick<CrewProfileView, 'canViewMedical'> | null,
  editor: Pick<CrewCardEditor, 'medical'>,
): CrewCardTab[] {
  const medical = mode === 'edit' && view?.canViewMedical === true && editor.medical !== undefined;
  return medical ? ['personal', 'physical', 'medical'] : ['personal', 'physical'];
}

/**
 * Sunucu hatasının metni: kart metni, yoksa Sign On metni ("Sign on now"; ör.
 * `CREW_ASSIGNMENT_OVERLAP`), yoksa genel metin.
 */
export function useCrewCardError() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    return t(crewCardErrorKey(code), {
      defaultValue: t(movementErrorKey(code), { defaultValue: t('crewProfile.errors.GENERIC') }),
    });
  };
}

/** "Sign on now" bölümünün seçenekleri (yalnız ofiste, Sign On yetkisiyle). */
export interface CrewCardSignOnOptions {
  vessels: readonly Option[];
  /** `null`: yükleniyor. */
  ranks: readonly RankOption[] | null;
  /** Kayıtlı aylık maaş alanı (`payroll.create`; ADR 0165 §3). */
  wage?: boolean;
}

/**
 * Personel kartının alanları (tasarım 3 "Personal Information", "Contact Information",
 * "Emergency Contact"): yeni kartta "Create login" anahtarıyla giriş e-postası ve işlemi yapanın
 * parolası da (ADR 0131 §1, Ek 1). `signOn` verilirse yeni kartta "Create login"ın üstünde
 * isteğe bağlı "Sign on now": Sign On penceresinin alanları; açıkken uyruk ve doğum tarihi
 * zorunlu (ADR 0131 §3, Ek 4 §2). Pencereden ayrı: testte ve düzenlemede aynı.
 */
export function CrewCardFields({
  form,
  mode,
  today,
  signOn,
  login = true,
  onChange,
}: {
  form: CrewCardForm;
  mode: Mode;
  /** Doğum tarihi en geç bugün. */
  today: string;
  signOn?: CrewCardSignOnOptions;
  /**
   * Yeni kartta "Create login" anahtarı (ADR 0151 Ek): hesap açma `users.create` + `users.edit`
   * ister; sunucu yok derse anahtar yok ("Sign on now" gibi).
   */
  login?: boolean;
  onChange: (form: CrewCardForm) => void;
}) {
  const { t, i18n } = useTranslation();
  const d = (key: string) => t(`crewProfile.dialog.${key}`);
  const locale = i18n.resolvedLanguage ?? 'en';
  const countries = useMemo(() => nationalityOptions(locale), [locale]);
  const set =
    <K extends keyof CrewCardForm>(key: K) =>
    (value: CrewCardForm[K]) =>
      onChange({ ...form, [key]: value });
  const text = (key: keyof CrewCardForm, extra: Record<string, unknown> = {}) => (
    <TextInput
      label={d(key)}
      value={String(form[key])}
      onChange={(event) => set(key)(event.currentTarget.value as never)}
      {...extra}
    />
  );
  // Sign On kuralı: kartta uyruk ve doğum tarihi (ADR 0131 §3)
  const signOnRequired = mode === 'new' && signOn !== undefined && form.signOnNow;
  const toggleSignOn = (checked: boolean) => {
    const only = signOn?.vessels.length === 1 ? (signOn.vessels[0]?.value ?? '') : '';
    onChange({
      ...form,
      signOnNow: checked,
      signOn:
        checked && form.signOn.vesselId === '' ? { ...form.signOn, vesselId: only } : form.signOn,
    });
  };
  const group = (legend: string, children: ReactNode) => (
    <fieldset className={classes.group}>
      <legend className={classes.legend}>{legend}</legend>
      <div className={classes.grid}>{children}</div>
    </fieldset>
  );
  return (
    <>
      {group(
        t('crewProfile.sections.personal'),
        <>
          {text('fullName', { required: true, maxLength: 200, className: classes.wide })}
          {text('crewCode', {
            placeholder: d('crewCodePlaceholder'),
            description: d('crewCodeHelp'),
            maxLength: 20,
          })}
          <Select
            label={d('nationality')}
            placeholder={d('nationalityPlaceholder')}
            data={countries}
            searchable
            clearable
            required={signOnRequired}
            value={form.nationality || null}
            onChange={(value) => set('nationality')(value ?? '')}
            comboboxProps={{ withinPortal: true }}
          />
          <DayInput
            label={d('dateOfBirth')}
            value={form.dateOfBirth || null}
            maxDate={today}
            clearable
            required={signOnRequired}
            onChange={(value) => set('dateOfBirth')(value ?? '')}
          />
          {text('placeOfBirth', { maxLength: 120 })}
          <Select
            label={d('gender')}
            data={(['M', 'F', 'X'] as const).map((value) => ({
              value,
              label: t(`crewProfile.gender.${value}`),
            }))}
            clearable
            value={form.gender || null}
            onChange={(value) => set('gender')(value ?? '')}
          />
          <Select
            label={d('maritalStatus')}
            data={(['single', 'married', 'other'] as const).map((value) => ({
              value,
              label: t(`crewProfile.marital.${value}`),
            }))}
            clearable
            value={form.maritalStatus || null}
            onChange={(value) => set('maritalStatus')(value ?? '')}
          />
        </>,
      )}
      {group(
        t('crewProfile.sections.contact'),
        <>
          {text('phone', { maxLength: 40, autoComplete: 'off' })}
          {text('contactEmail', {
            type: 'email',
            description: d('contactEmailHelp'),
            maxLength: 200,
            autoComplete: 'off',
          })}
          <Textarea
            className={classes.wide}
            label={d('address')}
            value={form.address}
            maxLength={300}
            autosize
            minRows={2}
            onChange={(event) => set('address')(event.currentTarget.value)}
          />
        </>,
      )}
      {group(
        d('emergency'),
        <>
          {text('emergencyName', { maxLength: 200 })}
          {text('emergencyRelation', { maxLength: 60 })}
          {text('emergencyPhone', { maxLength: 40 })}
        </>,
      )}
      {mode === 'new' && signOn ? (
        <div className={classes.login}>
          <Switch
            label={d('signOnNow')}
            description={d('signOnNowHelp')}
            checked={form.signOnNow}
            onChange={(event) => toggleSignOn(event.currentTarget.checked)}
          />
          {form.signOnNow ? (
            <div className={classes.grid}>
              <SignOnFields
                form={form.signOn}
                vessels={signOn.vessels}
                ranks={signOn.ranks}
                wage={signOn.wage === true}
                onChange={(patch) => onChange({ ...form, signOn: { ...form.signOn, ...patch } })}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {mode === 'new' && login ? (
        <div className={classes.login}>
          <Switch
            label={d('createLogin')}
            description={d('createLoginHelp')}
            checked={form.createLogin}
            onChange={(event) => set('createLogin')(event.currentTarget.checked)}
          />
          {form.createLogin ? (
            <div className={classes.grid}>
              <TextInput
                label={d('loginEmail')}
                type="email"
                required
                autoComplete="off"
                value={form.loginEmail}
                maxLength={320}
                onChange={(event) => set('loginEmail')(event.currentTarget.value)}
              />
              <PasswordInput
                label={d('password')}
                description={d('passwordHelp')}
                required
                autoComplete="current-password"
                value={form.password}
                onChange={(event) => set('password')(event.currentTarget.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * "+ Add Crew" ve kartta "Edit" penceresi (ADR 0131 §1). Yeni kartta hesap istendiyse geçici
 * parola aynı pencerede bir kez gösterilir (ADR 0042 §23); pencere kapanınca bellekten düşer.
 * `signOn` verilirse (ofis, Sign On yetkisi) "Sign on now" ile kart ve katılış tek gönderimde,
 * tek transaction'da yazılır (ADR 0131 Ek 4 §2); belge uyarıları Sign On'daki gibi gösterilir,
 * ardından geçici parola (varsa), sonra kart açılır.
 */
export function CrewCardDialog({
  mode,
  view = null,
  editor,
  today,
  signOn,
  login = true,
  onClose,
  onSaved,
}: {
  mode: Mode;
  view?: CrewProfileView | null;
  editor: CrewCardEditor;
  today: string;
  signOn?: { vessels: readonly Option[]; editor: CrewMovementEditor; wage?: boolean };
  /** Yeni kartta "Create login" anahtarı (`users.create` + `users.edit`; ADR 0151 Ek). */
  login?: boolean;
  onClose: () => void;
  onSaved: (view: CrewProfileView) => void;
}) {
  const { t } = useTranslation();
  const errorText = useCrewCardError();
  const [form, setForm] = useState<CrewCardForm>(() =>
    view ? formOfProfile(view) : emptyCrewCardForm(),
  );
  // Fotoğraf hemen yazılır (ADR 0158 §1): kartın son hâli ve sürümü burada; pencere kapanınca
  // ekran tazelenir
  const [latest, setLatest] = useState<CrewProfileView | null>(view);
  const [touched, setTouched] = useState(false);
  const [tab, setTab] = useState<CrewCardTab>('personal');
  const tabs = crewCardTabs(mode, view, editor);
  const [created, setCreated] = useState<CrewCardResult | null>(null);
  const [warningsSeen, setWarningsSeen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Rütbeler anahtar ilk açıldığında bir kez yüklenir
  const ranks = useRanks(signOn && form.signOnNow ? signOn.editor : undefined);
  const [loginOpen, setLoginOpen] = useState(false);
  const warnings = created && !warningsSeen ? (created.signOn?.warnings ?? []) : [];
  const temporary = created?.temporaryPassword ? created : null;
  // "Sign on now" ile hesapsız açılan kişi: hesap önerisi (ortak 2026-10-30 madde 9)
  const offer = created && !temporary && wantsLoginOffer(created) ? created : null;
  const finish = created
    ? () => onSaved(created.profile)
    : touched && latest
      ? () => onSaved(latest)
      : onClose;
  const afterWarnings = () => (temporary || offer ? setWarningsSeen(true) : finish());
  if (offer && loginOpen) {
    return (
      <CreateLoginDialog view={offer.profile} editor={editor} onClose={finish} onSaved={onSaved} />
    );
  }
  return (
    <Modal
      opened
      onClose={warnings.length > 0 ? afterWarnings : finish}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={
        warnings.length > 0 || offer
          ? t('crewProfile.dialog.created')
          : temporary
            ? t('setup.users.temp.title')
            : t(`crewProfile.dialog.${mode === 'new' ? 'newTitle' : 'editTitle'}`)
      }
      size={created ? 'md' : 'xl'}
      closeOnClickOutside={!created}
    >
      {warnings.length > 0 ? (
        <div className={classes.form}>
          <SignOnWarnings warnings={warnings} />
          <div className={classes.actions}>
            <Button onClick={afterWarnings}>{t('signOnOff.dialog.close')}</Button>
          </div>
        </div>
      ) : temporary?.temporaryPassword ? (
        <CreatedLoginPanel
          profile={temporary.profile}
          email={temporary.profile.email ?? ''}
          password={temporary.temporaryPassword}
          onDone={() => onSaved(temporary.profile)}
        />
      ) : offer ? (
        <LoginOffer
          name={offer.profile.fullName}
          onCreate={() => setLoginOpen(true)}
          onSkip={finish}
        />
      ) : (
        <form
          className={classes.form}
          onSubmit={async (event) => {
            event.preventDefault();
            if (!crewCardReady(form)) return;
            setBusy(true);
            setError(null);
            try {
              if (mode === 'new') {
                const result = await editor.create(newCrewCardInput(form));
                setForm((previous) => ({ ...previous, password: '' }));
                if (
                  result.temporaryPassword ||
                  (result.signOn?.warnings.length ?? 0) > 0 ||
                  wantsLoginOffer(result)
                ) {
                  setCreated(result);
                } else onSaved(result.profile);
              } else if (view) {
                onSaved(await editor.update(view.userId, crewCardUpdate(form, latest ?? view)));
              }
            } catch (caught) {
              setError(errorText(caught));
            } finally {
              setBusy(false);
            }
          }}
        >
          {mode === 'new' ? <p className={classes.help}>{t('crewProfile.dialog.help')}</p> : null}
          <Tabs
            value={tab}
            onChange={(value) => setTab((value as CrewCardTab | null) ?? 'personal')}
            keepMounted={false}
          >
            <Tabs.List>
              {tabs.map((id) => (
                <Tabs.Tab key={id} value={id}>
                  {t(`crewProfile.dialogTabs.${id}`)}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            <Tabs.Panel value="personal" pt="md">
              <div className={classes.form}>
                {mode === 'edit' && latest && editor.setPhoto && latest.canEditPhoto ? (
                  <CrewPhotoField
                    view={latest}
                    setPhoto={editor.setPhoto}
                    onChange={(next) => {
                      setLatest(next);
                      setTouched(true);
                    }}
                  />
                ) : null}
                <CrewCardFields
                  form={form}
                  mode={mode}
                  today={today}
                  {...(signOn
                    ? { signOn: { vessels: signOn.vessels, ranks, wage: signOn.wage === true } }
                    : {})}
                  login={login}
                  onChange={setForm}
                />
              </div>
            </Tabs.Panel>
            <Tabs.Panel value="physical" pt="md">
              <div className={classes.form}>
                <CrewPhysicalFields form={form} onChange={setForm} />
              </div>
            </Tabs.Panel>
            {tabs.includes('medical') && view && editor.medical ? (
              <Tabs.Panel value="medical" pt="md">
                <CrewMedicalEditor
                  userId={view.userId}
                  load={editor.medical}
                  {...(view.canEditMedical && editor.saveMedical
                    ? { save: editor.saveMedical }
                    : {})}
                />
              </Tabs.Panel>
            ) : null}
          </Tabs>
          {error ? (
            <p className={classes.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={classes.actions}>
            <Button variant="default" onClick={finish}>
              {t(`crewProfile.dialog.${tab === 'medical' ? 'close' : 'cancel'}`)}
            </Button>
            {/* Sağlık bilgisi kendi düğmesiyle yazılır (ayrı izin; ADR 0158 §2) */}
            {tab === 'medical' ? null : (
              <Button type="submit" loading={busy} disabled={!crewCardReady(form)}>
                {t(`crewProfile.dialog.${mode === 'new' ? 'create' : 'save'}`)}
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * "Remove card" (ADR 0142 §6): yanlış açılan, hiç görev almamış kişinin kartı kaldırılır. Kayıt
 * silinmez (soft delete; değişiklik kaydı kalır); işlemi yapanın parolası istenir (kullanıcı
 * yönetimi). Atama ya da kayıt varsa sunucu reddeder (`PERSON_HAS_RECORDS`).
 */
export function RemoveCardDialog({
  view,
  remove,
  onClose,
  onRemoved,
}: {
  view: CrewProfileView;
  remove: (userId: string, rowVersion: number, password: string) => Promise<void>;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`crewProfile.dialog.${key}`, options);
  const errorText = useCrewCardError();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={d('removeTitle', { name: view.fullName })}
      size="md"
    >
      <form
        className={classes.form}
        onSubmit={async (event) => {
          event.preventDefault();
          if (password === '') return;
          setBusy(true);
          setError(null);
          try {
            await remove(view.userId, view.rowVersion, password);
            onRemoved();
          } catch (caught) {
            setError(errorText(caught));
            setPassword('');
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={classes.help}>{d('removeHelp', { name: view.fullName })}</p>
        <PasswordInput
          label={d('password')}
          description={d('removePasswordHelp')}
          required
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.actions}>
          <Button variant="default" onClick={onClose}>
            {d('cancel')}
          </Button>
          <Button type="submit" color="red" loading={busy} disabled={password === ''}>
            {d('removeSubmit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Sign On sonrası hesap önerisi (ortak 2026-10-30 madde 9): kişinin hesabı yok ve işlemi yapanın
 * `users.create` + `users.edit` yetkisi var (ADR 0151 Ek). Kararı sunucu verir (`canCreateLogin`);
 * eski sunucu göndermezse öneri yok.
 */
export function loginOfferFor(view: Pick<CrewProfileView, 'hasLogin' | 'canCreateLogin'>): boolean {
  return !view.hasLogin && view.canCreateLogin === true;
}

/**
 * "+ Add Joining" (Sign On) sonrası öneri: kişinin kartı okunur; hesap yoksa ve yetki varsa kart
 * döner, yoksa `null`. Kart yazıcısı yoksa (gemi) ya da okuma başarısızsa öneri yok.
 */
export async function loginOfferAfterSignOn(
  source: Pick<PersonnelSource, 'crewProfile' | 'crewCards'>,
  userId: string,
): Promise<CrewProfileView | null> {
  if (!source.crewCards || userId === '') return null;
  try {
    const view = await source.crewProfile(userId);
    return loginOfferFor(view) ? view : null;
  } catch {
    return null;
  }
}

/** "New Crew Card ▸ Sign on now" sonucu: katılış yazıldı, hesap açılmadı ve öneri uygun. */
function wantsLoginOffer(result: CrewCardResult): boolean {
  return result.signOn !== undefined && !result.temporaryPassword && loginOfferFor(result.profile);
}

/** "Create login" penceresinin giriş e-postası: kartta iletişim e-postası varsa o. */
export function initialLoginEmail(view: { profile?: CrewProfileView['profile'] }): string {
  return view.profile?.contactEmail?.trim() ?? '';
}

/** "{{name}} has no login yet." ve "Create login now" / "Not now" (Sign On sonucu). */
export function LoginOffer({
  name,
  onCreate,
  onSkip,
}: {
  name: string;
  onCreate: () => void;
  onSkip?: () => void;
}) {
  const { t } = useTranslation();
  const o = (key: string, options: Record<string, unknown> = {}) =>
    t(`crewProfile.loginOffer.${key}`, options);
  return (
    <div className={classes.offer} role="status">
      <span>{o('text', { name })}</span>
      <Button size="sm" onClick={onCreate}>
        {o('create')}
      </Button>
      {onSkip ? (
        <Button size="sm" variant="default" onClick={onSkip}>
          {o('skip')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Hesap açıldıktan sonra bir kez gösterilen geçici parola (ADR 0131 Ek 1). Create login kişiyi
 * etkinleştirmez (ADR 0150 §5): devre dışı kişide panelin üstünde kimin etkinleştireceği yazar,
 * hazır mesajda ve kâğıtta "girişiniz hazır" satırı yoktur.
 */
export function CreatedLoginPanel({
  profile,
  email,
  password,
  onDone,
}: {
  profile: Pick<CrewProfileView, 'fullName' | 'active'>;
  email: string;
  password: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  return (
    <TemporaryPasswordPanel
      name={profile.fullName}
      email={email}
      password={password}
      {...(profile.active === false
        ? { inactiveNote: t('crewProfile.dialog.loginInactive', { name: profile.fullName }) }
        : {})}
      onDone={onDone}
    />
  );
}

/**
 * Kartta "Create login" (ADR 0131 Ek 1): gerçek giriş e-postası ve işlemi yapanın parolası;
 * geçici parola bir kez gösterilir. Kartta iletişim e-postası varsa alan onunla dolu gelir.
 */
export function CreateLoginDialog({
  view,
  editor,
  onClose,
  onSaved,
}: {
  /** Kart ya da Crew ▸ Login Accounts satırı (ADR 0147): kimlik, ad ve varsa kartın e-postası. */
  view: Pick<CrewProfileView, 'userId' | 'fullName'> & { profile?: CrewProfileView['profile'] };
  editor: Pick<CrewCardEditor, 'createLogin'>;
  onClose: () => void;
  onSaved: (view: CrewProfileView) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`crewProfile.dialog.${key}`, options);
  const errorText = useCrewCardError();
  const [email, setEmail] = useState(() => initialLoginEmail(view));
  const [password, setPassword] = useState('');
  const [created, setCreated] = useState<CrewCardResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = email.trim() !== '' && password !== '';
  return (
    <Modal
      opened
      onClose={created ? () => onSaved(created.profile) : onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={created ? t('setup.users.temp.title') : d('loginTitle')}
      size="md"
      closeOnClickOutside={!created}
    >
      {created?.temporaryPassword ? (
        <CreatedLoginPanel
          profile={created.profile}
          email={created.profile.email ?? email.trim().toLowerCase()}
          password={created.temporaryPassword}
          onDone={() => onSaved(created.profile)}
        />
      ) : (
        <form
          className={classes.form}
          onSubmit={async (event) => {
            event.preventDefault();
            if (!ready) return;
            setBusy(true);
            setError(null);
            try {
              setCreated(await editor.createLogin(view.userId, email.trim(), password));
              setPassword('');
            } catch (caught) {
              setError(errorText(caught));
            } finally {
              setBusy(false);
            }
          }}
        >
          <p className={classes.help}>{d('loginHelp', { name: view.fullName })}</p>
          <TextInput
            label={d('loginEmail')}
            type="email"
            required
            autoComplete="off"
            value={email}
            maxLength={320}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
          <PasswordInput
            label={d('password')}
            description={d('passwordHelp')}
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          {error ? (
            <p className={classes.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={classes.actions}>
            <Button variant="default" onClick={onClose}>
              {d('cancel')}
            </Button>
            <Button type="submit" loading={busy} disabled={!ready}>
              {d('loginSubmit')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
